import React, { useState, useEffect } from 'react';
import { api } from '../api/client';

export default function FeatureChecklist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const fetchItems = () => {
    api.get('/admin/feature-checklist').then(data => {
      setItems(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(fetchItems, []);

  const handleToggle = async (id, checked) => {
    // Optimistic update
    setItems(prev => prev.map(i => i.id === id ? { ...i, checked } : i));
    try {
      await api.put(`/admin/feature-checklist/${id}`, { checked });
      fetchItems(); // Refresh to get checked_by info
    } catch {
      // Revert on error
      setItems(prev => prev.map(i => i.id === id ? { ...i, checked: !checked } : i));
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all checklist items to unchecked?')) return;
    setMsg(null);
    try {
      await api.post('/admin/feature-checklist/reset');
      fetchItems();
      setMsg({ type: 'success', text: 'Checklist reset' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  // Group items by group_name
  const groups = {};
  items.forEach(item => {
    if (!groups[item.group_name]) groups[item.group_name] = [];
    groups[item.group_name].push(item);
  });

  const total = items.length;
  const checked = items.filter(i => i.checked).length;
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;

  if (loading) return <div className="card"><div className="empty-state">Loading checklist...</div></div>;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Feature Testing Checklist</h3>
        <button className="btn-danger btn-small" onClick={handleReset}>Reset All</button>
      </div>

      {msg && (
        <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 12, fontSize: 13,
          background: msg.type === 'success' ? 'rgba(72,187,120,0.1)' : 'rgba(252,129,129,0.1)',
          color: msg.type === 'success' ? 'var(--success)' : 'var(--danger)',
          border: `1px solid ${msg.type === 'success' ? 'var(--success)' : 'var(--danger)'}`,
        }}>
          {msg.text}
        </div>
      )}

      {/* Progress Bar */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span style={{ color: 'var(--text-secondary)' }}>Progress</span>
          <span style={{ color: pct === 100 ? 'var(--success)' : 'var(--warning)', fontWeight: 700 }}>
            {checked}/{total} ({pct}%)
          </span>
        </div>
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, height: 14, overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 8,
            background: pct === 100
              ? 'linear-gradient(90deg, var(--success), var(--warning))'
              : 'linear-gradient(90deg, var(--accent), var(--success))',
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Grouped Items */}
      {Object.entries(groups).map(([groupName, groupItems]) => {
        const groupChecked = groupItems.filter(i => i.checked).length;
        return (
          <div key={groupName} style={{ marginBottom: 16 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--border)',
            }}>
              <h4 style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>{groupName}</h4>
              <span style={{ fontSize: 12, color: groupChecked === groupItems.length ? 'var(--success)' : 'var(--text-muted)' }}>
                {groupChecked}/{groupItems.length}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {groupItems.map(item => (
                <label
                  key={item.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                    background: item.checked ? 'rgba(72,187,120,0.05)' : 'var(--bg-secondary)',
                    border: `1px solid ${item.checked ? 'rgba(72,187,120,0.2)' : 'var(--border)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={e => handleToggle(item.id, e.target.checked)}
                    style={{ width: 'auto', accentColor: 'var(--success)' }}
                  />
                  <span style={{
                    flex: 1, fontSize: 13,
                    color: item.checked ? 'var(--success)' : 'var(--text-secondary)',
                    textDecoration: item.checked ? 'line-through' : 'none',
                  }}>
                    {item.item_label}
                  </span>
                  {item.checked && item.checked_by_name && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {item.checked_by_name}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
